import Contract from './contract';
import ICompany from './company';
import { IDocument } from './document';
import { SavV2DocumentsData } from './savV2Documents';
import { ISavV2Quote } from './savV2Quote';
import { ISavV2Intervention } from './savV2Intervention';
import { ISavV2Decision } from './savV2Decision';
import { SavV2Event } from './savV2Event';
import { IncidentInBusiness } from './incidentInBusiness';

// Timeline bénéficiaire (calculée par l'API à partir des VirtualStatus)
export type BeneficiaryStepKey =
  | 'declaration'
  | 'analysis'
  | 'decision'
  | 'treatment'
  | 'closure';

export type BeneficiaryStepState =
  | 'completed'
  | 'current'
  | 'pending'
  | 'closed_positive'
  | 'closed_negative';

export interface IBeneficiaryProgressStep {
  key: BeneficiaryStepKey;
  label: string;
  state: BeneficiaryStepState;
}

export interface IBeneficiaryProgressAction {
  type: 'PROVIDE_QUOTE' | 'SIGN' | 'OTHER';
  label: string;
  urgent: boolean;
}

export interface IBeneficiaryProgress {
  visible: boolean;
  currentStep: number; // 0..4
  outcome: 'in_progress' | 'closed_positive' | 'closed_negative';
  phaseLabel: string;
  action: IBeneficiaryProgressAction | null;
  steps: IBeneficiaryProgressStep[]; // toujours 5 éléments, dans l'ordre
}

// Virtual Status Info (calculé par l'API)
export interface VirtualStatusInfo {
  status: string;
  label: string;
  description?: string;
  phase?: string;
  actionRequired?: string;
  priority: number;
}

export enum SavStatus {
  TEMPORAIRE = 1,
  NOUVEAU = 2,
  EN_ATTENTE_BENEFICIAIRE = 3,
  EN_ATTENTE_GESTIONNAIRE = 4,
  LITIGE = 5,
  EN_ATTENTE_GARANT = 6,
  A_PRENDRE_EN_CHARGE = 7,
  PRIS_EN_CHARGE = 8,
  SANS_SUITE = 9,
}

export interface RepairerContact {
  id: number;
  savId: number;
  interventionId?: string;
  repairerUuid: string;
  repairerName: string;
  repairerPhone?: string;
  repairerEmail?: string;
  outcome: 'available' | 'unavailable' | 'no_answer' | 'pending';
  notes?: string;
  contactedBy?: string;
  nextActionDate?: string;
  responseDelay?: number;
  contactMethod?: string;
  createdAt: string;
  updatedAt: string;
}

export enum SavV2Status {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export interface ISavV2 {
  id: number;
  uniqueId: string;
  status: SavV2Status; // L'API renvoie "status" pas "v2Status"
  stateId?: SavStatus; // État du sinistre (ID_ETAT_SINISTRE dans la DB)
  contractId: number;
  contract?: Contract;

  // Dates
  createdAt: string;
  updatedAt: string;
  declarationDate?: string;
  incidentDate?: string;
  draftUpdatedAt?: string;

  // Step 1 - Description
  description?: string;
  mileage?: number;
  faultLocation?: string;
  faultOccurrence?: string;
  faultManifestation?: string;
  symptoms?: string[];
  canDrive?: boolean;
  noMileagePhotoReason?: string;

  // Step 2 - Localisation
  incidentAddress?: string;
  incidentLatitude?: number;
  incidentLongitude?: number;
  incidentPostalCode?: string;
  incidentCity?: string;
  incidentRegion?: string;
  incidentCountry?: string;
  incidentInBusiness?: IncidentInBusiness;

  currentInterventionId?: string;
  currentQuoteId?: string;

  // ==================== SIGNATURE FLAGS (computed bene-side) ====================
  /** true si la décision courante est PENDING_BENEFICIARY_SIGNATURE */
  awaitingMySignature?: boolean;
  /** Document.id du bon de décision signé final si la décision est SIGNED, null sinon */
  signedBonDeDecisionId?: number | null;

  // ==================== LEGAL BLOCKING ====================
  legalBlocked: boolean;
  legalBlockedAt?: string;
  legalBlockedBy?: string;  // UUID du gestionnaire
  legalBlockedReason?: string;
  legalBlockedLetterDate?: string;
  legalUnblockedAt?: string;
  legalUnblockedBy?: string;  // UUID du gestionnaire
  legalUnblockedReason?: string;

  // ==================== MANAGER ASSIGNMENT ====================
  primaryManagerId?: string;  // UUID du gestionnaire principal
  primaryManager?: {
    uuid: string;
    firstName: string;
    lastName: string;
    email: string;
  };

  savManagerId?: string;  // UUID du gestionnaire SAV
  savManager?: {
    uuid: string;
    firstName: string;
    lastName: string;
    email: string;
  };

  // ==================== AWAITING FLAGS (Phase 3) ====================
  awaitingBeneficiary?: boolean;
  awaitingManager?: boolean;
  awaitingGarant?: boolean;

  // ==================== CYCLE PAIEMENT RÉPARATEUR ====================
  // Snapshot figé à la finalisation bénéficiaire (Bloc 5.0.bis).
  // Détermine si ce SAV suit le cycle paiement — jamais lu de la config
  // live du garant. Cf SPECS/repairer_payment.
  paymentCycleEnabled?: boolean;

  // ==================== NEW MESSAGE FLAGS ====================
  hasNewBeneficiaryMessage?: boolean;
  hasNewGarantMessage?: boolean;
  hasNewInternalMessage?: boolean;
  hasNewMessageForBeneficiary?: boolean;
  // Compteur de messages gestionnaire non lus par le garant (badge outil-pro).
  // Reset à 0 quand le garant ouvre le dossier ou répond.
  unreadGarantMessagesCount?: number;
  internalMessageReminderDate?: string; // Date de rappel pour les messages internes (ISO string)

  // Step 3 - État de réparation
  repairStatus?: string;
  hasQuote?: boolean;

  // Documents uploadés par le bénéficiaire (stockés en JSON dans v2_documents)
  documentsData?: SavV2DocumentsData;

  // Documents partagés avec le bénéficiaire (table METIER_A_POUR_DOCUMENT)
  documentsSharedWithBeneficiary?: IDocument[];

  // Documents internes accessibles uniquement aux gestionnaires (table METIER_A_POUR_DOCUMENT_INTERNAL)
  documentsInternal?: IDocument[];

  // ==================== RELATIONS COMPLETES ====================

  // Garantisseur (tête de groupe si contractGuarantor === 2, sinon undefined)
  // Chargé par l'API quand l'agence a un parent qui est garantisseur
  guarantorCompany?: ICompany;

  // Intervention actuelle
  currentIntervention?: ISavV2Intervention;

  // Devis actuel
  currentQuote?: ISavV2Quote;

  // Toutes les interventions
  interventions?: ISavV2Intervention[];

  // Tous les devis
  quotes?: ISavV2Quote[];

  // Tous les événements
  events?: SavV2Event[];

  // ==================== DONNEES CALCULEES ====================

  // Validation data (calculée à partir des décisions)
  validationData?: {
    hasValidDecision: boolean;
    lastDecision?: ISavV2Decision;
    totalAmount?: number;
    coveredAmount?: number;
  };

  // Signature data (calculée à partir des décisions)
  signatureData?: {
    hasBeneficiarySignature: boolean;
    hasGarantSignature: boolean;
    signedAt?: string;
  };

  // Reimbursement data (calculée à partir des événements ou autre logique)
  reimbursementData?: {
    isReimbursed: boolean;
    reimbursedAt?: string;
    amount?: number;
  };

  // Virtual statuses (calculés par l'API via les stratégies)
  virtualStatuses?: VirtualStatusInfo[];

  // Timeline bénéficiaire (calculée par l'API, exposée au bénéficiaire uniquement)
  beneficiaryProgress?: IBeneficiaryProgress;

  // Contacts réparateurs (chargés par l'API)
  repairerContacts?: RepairerContact[];

  // Events réparateur (chargés par l'API)
  repairerEvents?: SavV2Event[];

  // V1 legacy fields
  repairerUuid?: string;
  decisionVoucherDocId?: number;
  decisionVoucher?: any;
  acceptedQuoteId?: number;
  amountSeller?: number;
  amountSellerCommercial?: number;
  amountBeneficiary?: number;

  // Ancien réparateur (chargé par l'API si changement détecté)
  previousRepairer?: {
    company: ICompany;
    assignedAt: string;
    invalidatedAt: string | null;
  } | null;
}